import type { User } from '@squats/shared';
import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect } from 'react';
import { db } from '../log/db.ts';
import { refreshPack, syncNow, useSyncStatus } from '../log/sync.ts';

/** How many of your changes are saved here but not uploaded yet. */
/** Changes on this device waiting to upload (yours, and any you logged for a training partner). */
export function usePendingCount() {
  return (
    useLiveQuery(async () => {
      const waiting = (r: { rejected?: string }) => !r.rejected;
      const [w, s] = await Promise.all([db.workouts.where('dirty').equals(1).filter(waiting).count(), db.sets.where('dirty').equals(1).filter(waiting).count()]);
      return w + s;
    }) ?? 0
  );
}

/**
 * Keeps uploads moving and the offline pack fresh: on open, on reconnect, when the app comes back to the
 * foreground (iPhones have no background sync), and every 30 s while anything is waiting.
 */
export function SyncRunner({ user, online }: { user: User; online: boolean }) {
  const pending = usePendingCount();

  useEffect(() => {
    if (!online) return;
    const run = () => {
      void syncNow().then(() => refreshPack(user).catch(() => {}));
    };
    run();
    const onVisible = () => document.visibilityState === 'visible' && run();
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [user, online]);

  useEffect(() => {
    if (!online || !pending) return;
    const t = setInterval(() => void syncNow(), 30_000);
    return () => clearInterval(t);
  }, [user.id, online, pending]);

  return null;
}

/** "3 changes waiting to upload", shown only while something is waiting. */
export function PendingLine({ online }: { online: boolean }) {
  const pending = usePendingCount();
  const { error, unreachable } = useSyncStatus();
  if (!pending) return null;
  const what = `${pending} ${pending === 1 ? 'change' : 'changes'} saved on this phone`;
  return (
    <p className={`banner pending${error ? ' failed' : ''}`} role="status">
      {error ? `${what}. Upload failed: ${error}` : online && !unreachable ? `${what}, uploading…` : `${what}. ${pending === 1 ? 'It' : 'They'}’ll upload when there’s a connection.`}
    </p>
  );
}
